import styles from './style/layout.module.scss';
import InnerLayout from '../Layout/InnerLayout';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineSetting } from 'react-icons/ai';
import { getServices, updateDetail, selectMenu } from '../../store/slice/manager/menu';
import { getServiceName } from '../../constant/SERVICE_NAME_MAP';
import Button from '../Utilities/Button';
import Modal from '../Utilities/Modal';
import AlertSwitch from '../Utilities/AlertSwitch';
import Alert from '../Utilities/Alert';
import Toast from '../Utilities/Toast';
import Dropdown from '../Utilities/DropdownDynamic';
import ServiceEditModal from './ServiceEditModal';

// 「企業帳號」不是 software service，固定列為第一列、不受狀態篩選影響，
// 開關/負責人/使用統計欄顯示「—」，編輯只開企業帳號開通設定。
const ENTERPRISE_ROW = { id: 0, isEnterprise: true };

const FILTER_OPTIONS = [
  { key: 'enabled', label: '啟用中' },
  { key: 'disabled', label: '停用中' },
  { key: 'all', label: '全部' },
];

const ServiceList = () => {
  const dispatch = useDispatch();
  const { services } = useSelector(selectMenu);

  const [filter, setFilter] = useState('enabled'); // 預設只看啟用中
  const [editId, setEditId] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null); // 待確認啟用/停用的服務
  const [toast, setToast] = useState({ show: false, error: false, title: '', subtitle: '' });

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 2500);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const representativeEmail = (s) => s?.representative?.user_email || s?.representative_user?.email || '尚未設定';

  // 服務對象：以方案（avaliable_plans）的 type 判斷。
  // 只要有 center 方案代表可供核心（中控）使用 → 「核心/企業」；全為 company → 「企業」。
  // 無已發佈方案時回傳 null，不標注。
  const serviceAudience = (s) => {
    const plans = s?.avaliable_plans || [];
    if (!plans.length) return null;
    return plans.some((p) => p?.type === 'center') ? 'center' : 'company';
  };

  // 服務對象排序：核心/企業（center）在前，企業（company）次之，未標注（null）最後
  const audienceRank = (s) => {
    const audience = serviceAudience(s);
    if (audience === 'center') return 0;
    if (audience === 'company') return 1;
    return 2;
  };

  const filteredServices = (services || [])
    .filter((s) => s.id !== 8)
    .filter((s) => {
      if (filter === 'enabled') return !!s.status;
      if (filter === 'disabled') return !s.status;
      return true;
    })
    .sort((a, b) => audienceRank(a) - audienceRank(b));

  const handleToggleConfirm = () => {
    const s = toggleTarget;
    if (!s) return;
    const next = !s.status;
    const name = getServiceName(s.name);
    dispatch(updateDetail({ service: s.id, activate_mode: s.activate_mode, status: next })).then((res) => {
      const msg = res?.payload?.msg;
      if (msg === 'success') {
        setToast({
          show: true,
          error: false,
          title: next ? `已啟用${name}於KEYSTONE系統` : `已停用${name}於KEYSTONE系統`,
          subtitle: name,
        });
        dispatch(getServices());
      } else {
        setToast({
          show: true,
          error: true,
          title:
            msg === 'Cannot enable the service!'
              ? `「${name}」尚未發佈可用方案，無法啟用服務`
              : msg === 'Cannot disable the service!'
              ? `「${name}」尚有企業帳號使用中，無法停用服務`
              : `「${name}」無法變更服務啟用狀態`,
          subtitle: name,
        });
      }
    });
    setToggleTarget(null);
  };

  return (
    <InnerLayout>
      <div className={styles.dashboardBusinessRight}>
        <div className={styles.dashboardTitle}>服務管理</div>

        <div className={styles.dashboardListContainer}>
          <div className={styles.dashboardListTitle}>服務列表</div>
          <div className={styles.dropdownWrapper}>
            <Dropdown
              value={FILTER_OPTIONS.find((o) => o.key === filter)?.label}
              listItems={FILTER_OPTIONS}
              caption='label'
              onChange={(e) => setFilter(e.key)}
              wide
            />
          </div>
        </div>

        <div className={`${styles.tableContainer} ${styles.serviceListTable}`}>
          <table>
            <thead>
              <tr>
                <th>服務名稱</th>
                <th>服務啟用狀態</th>
                <th>負責人</th>
                <th>啟用中企業帳號總數</th>
                <th>總使用人數</th>
                <th>服務對象</th>
                <th width='8%'></th>
              </tr>
            </thead>
            <tbody>
              {/* 企業帳號固定第一列 */}
              <tr>
                <td className={styles.tableTd}>企業帳號</td>
                <td className={styles.tableTd}>—</td>
                <td className={styles.tableTd}>—</td>
                <td className={styles.tableTd}>—</td>
                <td className={styles.tableTd}>—</td>
                <td className={styles.tableTd}>—</td>
                <td className={`${styles.tableTd} ${styles.actionCell}`}>
                  <Button
                    type='tertiary'
                    title='編輯'
                    icon={<AiOutlineSetting />}
                    onClick={() => setEditId(String(ENTERPRISE_ROW.id))}
                  />
                </td>
              </tr>

              {filteredServices.map((data) => {
                const audience = serviceAudience(data);
                return (
                <tr key={data.id}>
                  <td className={styles.tableTd}>{getServiceName(data.name)}</td>
                  <td className={styles.tableTd}>
                    <div className={styles.statusCell}>
                      <span className={`${styles.statusBadge} ${data.status ? styles.on : styles.off}`}>
                        {data.status ? '啟用中' : '停用中'}
                      </span>
                      <AlertSwitch
                        isChecked={!!data.status}
                        color={{ on: '#34c759', off: '#dddddd' }}
                        onChange={() => setToggleTarget(data)}
                      />
                    </div>
                  </td>
                  <td className={styles.tableTd}>{representativeEmail(data)}</td>
                  <td className={styles.tableTd}>{data.enterprises_using_service_count ?? 0}</td>
                  <td className={styles.tableTd}>{data.users_using_service_count ?? 0}</td>
                  <td className={styles.tableTd}>
                    {audience ? (
                      <span className={`${styles.audiencePill} ${styles[audience]}`}>
                        {audience === 'center' ? '核心/企業' : '企業'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={`${styles.tableTd} ${styles.actionCell}`}>
                    <Button
                      type='tertiary'
                      title='編輯'
                      icon={<AiOutlineSetting />}
                      onClick={() => setEditId(String(data.id))}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Alert
        isOpen={!!toggleTarget}
        onConfirm={handleToggleConfirm}
        onClose={() => setToggleTarget(null)}
        rightButtonText={toggleTarget?.status ? '停用' : '啟用'}
        leftButtonText='取消'
        mainTitle={
          toggleTarget?.status
            ? `停用「${getServiceName(toggleTarget?.name)}於KEYSTONE系統」`
            : `啟用「${getServiceName(toggleTarget?.name)}於KEYSTONE系統」`
        }
      />

      <Toast show={toast.show} title={toast.title} subtitle={toast.subtitle} error={toast.error} />

      <Modal isOpen={editId !== null} size='xxl'>
        {editId !== null && (
          <ServiceEditModal
            serviceId={editId}
            onClose={() => {
              setEditId(null);
              // 關閉後重抓列表，讓狀態/負責人/統計即時反映。
              dispatch(getServices());
            }}
          />
        )}
      </Modal>
    </InnerLayout>
  );
};

export default ServiceList;
